import CircleIcon from "@mui/icons-material/Circle";
// import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { Box } from "@mui/material";
import { EventProps } from "react-big-calendar";
import Flex from "../../../../../components/Flex";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";
import { AppointmentSchedulingType } from "../../../../../dummy-data/appointment-list-data-scheduling";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { formatDate, formatTime } from "../../../../../utils/date-time-utility";
import { getAge } from "../../../../../utils/utils";
import { ViewType } from "../SchedulingLayout";
import {
  APPOINTMENT_STATUS_RECORD,
  COLOR_MAPPER,
} from "../appointment/AppointmentScheduling";

export const ShowMoreDrawer = ({
  disclosureHost,
  onEvenClick,
  isMultipleProviders,
}: {
  disclosureHost: DisclosureHost;
  onEvenClick: (appointment: any) => void;
  isMultipleProviders?: boolean;
}) => {
  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      // Closing clears `data` in the same render that closes the dialog, so a
      // fade-out leaves it on screen briefly with no appointments and an
      // "undefined" date. Drop the exit transition so it goes at once.
      transitionDuration={{ exit: 0 }}
      dialogTitle={`All Appointments for ${formatDate(disclosureHost?.data?.[0]?.startTime)}`}
    >
      <Flex flexDirection={"column"} gap={1}>
        {disclosureHost?.data?.map((it: any) => {
          return (
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => {
                disclosureHost.close();
                onEvenClick(it);
              }}
              width={"25rem"}
            >
              {eventWrapper(it, "month", isMultipleProviders)}
            </Box>
          );
        })}
      </Flex>
    </StyledDialog>
  );
};

export function eventWrapper(
  props: EventProps,
  view?: ViewType,
  isMultipleProviders?: boolean
) {
  const statusLabel =
    APPOINTMENT_STATUS_RECORD[
      ((props.event as any)?.status as AppointmentSchedulingType["status"]) ||
        (props as any)?.status
    ];

  const activeStatusColor = COLOR_MAPPER[statusLabel];
  // const appointmentType =
  //   NEW_APPOINTMENT_TYPE_MAPPER[
  //     ((props.event as any)?.type as AppointmentSchedulingType["type"]) ||
  //       (props as any)?.type
  //   ];

  const CurDate = () =>
    ((props as any)?.startTime || (props as any)?.slotStart) &&
    ((props as any)?.endTime || (props as any)?.slotEnd) && (
      <Text>
        {`${formatTime(
          (props as any)?.event?.startTime || (props as any)?.slotStart
        )}`}
      </Text>
    );

  const DateWithName = () => (
    <Flex flexDirection="column" gap={0.3}>
      <Text sx={{ fontSize: "12px" }}>
        {`${formatTime(
          (props as any)?.event?.startTime ||
            (props as any)?.startTime ||
            (props as any)?.slotStart
        )}${
          (props as any)?.event?.endTime ||
          (props as any)?.endTime ||
          (props as any)?.slotEnd
            ? ` - ${formatTime(
                (props as any)?.event?.endTime ||
                  (props as any)?.endTime ||
                  (props as any)?.slotEnd
              )}`
            : ""
        }`}
      </Text>
      <Text
        sx={{
          fontSize: "12px",
          fontWeight: 500,
        }}
        color={activeStatusColor?.color}
      >
        {statusLabel}
      </Text>
    </Flex>
  );

  const CurrentActiveMode = () =>
    MODE_ICON_MAPPER[
      ((props?.event as any)?.mode ||
        (props as any)?.mode) as AppointmentSchedulingType["mode"]
    ];

  return (
    <Flex
      key={(props as any)?.startTime?.toString()}
      height={"100%"}
      width={"100%"}
      borderRadius={"8px"}
      bgcolor={
        isMultipleProviders ? "white" : activeStatusColor?.bgColor || "#F3F4F6"
      }
      id="comp"
      mb={0.2}
      alignItems={isMultipleProviders ? "center" : undefined}
      overflow="hidden"
    >
      {isMultipleProviders ? (
        <CircleIcon
          sx={{
            color:
              (props?.event as any)?.providerColourCode ||
              (props as any)?.providerColourCode,
            fontSize: "14px",
          }}
        />
      ) : (
        <Box
          minWidth={"4px"}
          borderRadius={"4px 0 0 4px"}
          bgcolor={activeStatusColor?.color}
        />
      )}
      <Flex
        gap={isMultipleProviders || view !== "month" ? 0 : 0.4}
        ml={1}
        py={0.5}
        flexDirection={"column"}
        justifyContent={view !== "month" ? "center" : "normal"}
      >
        <Flex gap={1}>
          <CurrentActiveMode />
          <Text sx={{ fontWeight: 500 }}> {props.title}</Text>
          {view === "day" && <DayViewEvent props={props} />}

          {isMultipleProviders && view === "month" && <CurDate />}
        </Flex>
        {!isMultipleProviders && <DateWithName />}
      </Flex>
    </Flex>
  );
}

const DayViewEvent = ({ props }: { props: EventProps }) => {
  const age = getAge((props?.event as any)?.patientDob);
  const gender = (props?.event as any)?.patientGender
    ?.toUpperCase()
    ?.slice(0, 1);
  return <Text>{`(${age}${gender ? `, ${gender}` : ""})`}</Text>;
};

export const WeekEventCard = ({ props }: { props: EventProps }) => {
  const event = props.event as any;
  const statusLabel =
    APPOINTMENT_STATUS_RECORD[
      event?.status as AppointmentSchedulingType["status"]
    ];
  const activeStatusColor = COLOR_MAPPER[statusLabel];
  const startMs = new Date(event?.startTime).getTime();
  const endMs = new Date(event?.endTime).getTime();
  const durationMin = (endMs - startMs) / 60000;
  const isLong = durationMin > 30;
  const age = getAge(event?.patientDob);
  const gender = event?.patientGender?.toUpperCase()?.slice(0, 1);

  return (
    <Flex
      height="100%"
      width="100%"
      borderRadius="6px"
      bgcolor={activeStatusColor?.bgColor || "#F3F4F6"}
      overflow="hidden"
    >
      <Box
        minWidth="3px"
        height="100%"
        borderRadius="6px 0 0 6px"
        bgcolor={activeStatusColor?.color}
      />
      <Flex
        flexDirection="column"
        justifyContent={isLong ? "flex-start" : "center"}
        px={0.75}
        py={isLong ? 0.75 : 0.25}
        overflow="hidden"
        minWidth={0}
      >
        <Flex alignItems="center" gap={0.5}>
          {WEEK_MODE_ICON[event?.mode]}
          <Text
            sx={{
              fontWeight: 500,
              fontSize: isLong ? "12px" : "11px",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {props.title}
            {isLong && age ? ` (${age}${gender ? `, ${gender}` : ""})` : ""}
          </Text>
        </Flex>
        <Text
          sx={{
            fontSize: isLong ? "11px" : "10px",
            lineHeight: 1.4,
            color: "#6B7280",
            whiteSpace: "nowrap",
            mt: isLong ? 0.5 : 0,
          }}
        >
          {formatTime(event?.startTime)} - {formatTime(event?.endTime)}
        </Text>
        <Text
          sx={{
            fontSize: isLong ? "11px" : "10px",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
          color={activeStatusColor?.color}
        >
          {statusLabel}
        </Text>
      </Flex>
    </Flex>
  );
};

const WEEK_MODE_ICON: Record<string, React.ReactNode> = {
  IN_PERSON: <PersonOutlinedIcon sx={{ fontSize: "12px" }} color="primary" />,
  VIRTUAL: <VideocamOutlinedIcon sx={{ fontSize: "12px" }} color="primary" />,
};

const DAY_MODE_ICON: Record<string, React.ReactNode> = {
  IN_PERSON: <PersonOutlinedIcon sx={{ fontSize: "14px" }} color="primary" />,
  VIRTUAL: <VideocamOutlinedIcon sx={{ fontSize: "14px" }} color="primary" />,
};

export const SchedulingDayEventCard = ({ props }: { props: EventProps }) => {
  const event = props.event as any;
  const startMs = new Date(event?.startTime).getTime();
  const endMs = new Date(event?.endTime).getTime();
  const durationMin = (endMs - startMs) / 60000;
  const statusLabel =
    APPOINTMENT_STATUS_RECORD[
      event?.status as AppointmentSchedulingType["status"]
    ];
  const activeStatusColor = COLOR_MAPPER[statusLabel];
  const age = getAge(event?.patientDob);
  const gender = event?.patientGender?.toUpperCase()?.slice(0, 1);

  // Long events (> 15 min): full layout
  if (durationMin > 15) {
    return eventWrapper(props, "day", false);
  }

  // Short events (≤ 15 min): single horizontal row
  return (
    <Flex
      height="100%"
      width="100%"
      borderRadius="8px"
      bgcolor={activeStatusColor?.bgColor || "#F3F4F6"}
      alignItems="center"
    >
      <Box
        minWidth="4px"
        height="100%"
        borderRadius="4px 0 0 4px"
        bgcolor={activeStatusColor?.color}
      />
      <Flex
        ml={0.5}
        alignItems="center"
        gap={1}
        overflow="hidden"
        flexWrap="nowrap"
        width="100%"
        pr={1}
      >
        <Text
          sx={{
            fontWeight: 500,
            fontSize: "12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {`${props.title} (${age}${gender ? `, ${gender}` : ""})`}
        </Text>
        {DAY_MODE_ICON[event?.mode]}
        <Text
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
          color={activeStatusColor?.color}
        >
          {statusLabel}
        </Text>
        <Text
          sx={{
            fontSize: "12px",
            whiteSpace: "nowrap",
            color: "#6B7280",
            marginLeft: "auto",
          }}
        >
          {`${formatTime(event?.startTime)} - ${formatTime(event?.endTime)}`}
        </Text>
      </Flex>
    </Flex>
  );
};

const MODE_ICON_MAPPER: Record<
  AppointmentSchedulingType["mode"],
  React.ReactNode
> = {
  IN_PERSON: <PersonOutlinedIcon fontSize="small" color="primary" />,
  VIRTUAL: <VideocamOutlinedIcon fontSize="small" color="primary" />,
};
